import { Injectable, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import firebase from 'firebase/compat/app';
import { EmailAuthProvider, reauthenticateWithCredential } from '@angular/fire/auth';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { StorageItems } from '../constants/storage-items.constants';
import { Router } from '@angular/router';
import { SubjectStatisticsCacheService } from './subject-statistics-cache.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private authStateChangeSubscription: Subscription | null = null;
  private sub: Subscription | null = null;
  private currentUserId: string | null = null;

  loggedIn: boolean = false;
  user: firebase.User | null = null;

  constructor(
    private afAuth: AngularFireAuth,
    private afFun: AngularFireFunctions,
    private router: Router,
    private statisticsCache: SubjectStatisticsCacheService
  ) {
    this.authStateChangeSubscription = this.afAuth.authState.subscribe(user => {
      if (!user || user.uid !== this.currentUserId) this.statisticsCache.clear();
      // Check if a different user logged in from another tab
      if (user && this.currentUserId && user.uid !== this.currentUserId) {
        window.location.reload();
      }

      this.user = user;
      if (user) {
        this.currentUserId = user.uid;
        localStorage.setItem(StorageItems.USER, JSON.stringify(user));
        localStorage.setItem(StorageItems.USERID, user.uid);
      }
      else {
        localStorage.removeItem(StorageItems.USER);
        localStorage.removeItem(StorageItems.USERID);
        localStorage.removeItem(StorageItems.USER_ID_FOR_ADMIN);

        // Don't redirect to login if we're on public routes that need to work without authentication
        // Check both current URL and target URL (if navigation is in progress)
        const currentUrl = this.router.url;
        const targetUrl = this.router.getCurrentNavigation()?.finalUrl?.toString() || currentUrl;

        const isPublicRoute = targetUrl.includes('/email-confirmation/') ||
          targetUrl.includes('/password-reset/') ||
          targetUrl.includes('/login') ||
          targetUrl.includes('/registration') ||
          targetUrl.includes('/forgot-pass') ||
          targetUrl.includes('/profile-delete-help');

        if (!isPublicRoute) {
          this.router.navigate(['/login'], { replaceUrl: true });
        }
        this.currentUserId = null;
      }
    })
  }

  async ngOnDestroy(): Promise<void> {
    this.sub?.unsubscribe();
    this.authStateChangeSubscription?.unsubscribe();
  }

  async login(email: string, password: string) {
    const res = await this.afAuth.signInWithEmailAndPassword(email, password);
    return res;
  }

  async getCustomClaims(): Promise<any> {
    const user = await this.afAuth.currentUser;
    if (user) {
      const token = await user.getIdTokenResult();
      return token.claims;
    }
    return null;
  }

  async logout() {
    try {
      this.loggedIn = false;
      this.user = null;
      this.currentUserId = null;

      await this.fbLogout();

      // Remove specific items instead of clearing all localStorage
      localStorage.removeItem(StorageItems.USER);
      localStorage.removeItem(StorageItems.USERID);
      localStorage.removeItem(StorageItems.USER_ID_FOR_ADMIN);
      // LAST_SELECTED_SUBJECT is intentionally preserved
    } catch (error) {
      console.error('Logout error:', error);
      this.user = null;
      this.currentUserId = null;
      localStorage.removeItem(StorageItems.USER);
      localStorage.removeItem(StorageItems.USERID);
      localStorage.removeItem(StorageItems.USER_ID_FOR_ADMIN);
    }
  }

  currentUser(): Observable<firebase.User | null> {
    return this.afAuth.user;
  }

  async getCurrentUserUID(): Promise<string | undefined> {
    return (await firstValueFrom(this.afAuth.authState))?.uid;
  }

  getAFAuth() {
    return this.afAuth;
  }

  async newPassword(newPassword: string) {
    return this.afAuth.currentUser.then(user => user?.updatePassword(newPassword));
  }

  async newEmail(newEmail: string) {
    return this.afAuth.currentUser.then(user => user?.updateEmail(newEmail));
  }

  async updateCurrentUserName(name: string) {
    return this.afAuth.currentUser.then(user =>
      user?.updateProfile({
        displayName: name,
      })
    );
  }

  async passwordRemind(email: string) {
    return this.afAuth.sendPasswordResetEmail(email);
  }

  async createUser(email: string, password: string, name?: string) {
    return this.afAuth.createUserWithEmailAndPassword(email, password).then(result => {
      if (name) {
        this.updateCurrentUserName(name);
      }
      return result.user;
    });
  }

  async fbLogout() {
    this.statisticsCache.clear();
    await this.afAuth.signOut();
  }

  async signInWithGoogle(): Promise<firebase.auth.UserCredential> {
    const provider = new firebase.auth.GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');
    return this.afAuth.signInWithPopup(provider);
  }

  async signInWithApple(): Promise<firebase.auth.UserCredential> {
    const provider = new firebase.auth.OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    return this.afAuth.signInWithPopup(provider);
  }

  async signInWithNeptun(): Promise<firebase.auth.UserCredential> {
    const provider = new firebase.auth.SAMLAuthProvider('saml.szte-neptun');
    return this.afAuth.signInWithPopup(provider);
  }

  async updatePassword(oldPassword: string, password: string, confirmPassword: string): Promise<any> {
    const user = await this.afAuth.currentUser;
    if (user && user.email) {
      try {
        const credential = EmailAuthProvider.credential(user.email, oldPassword);
        await reauthenticateWithCredential(user, credential);

        return await firstValueFrom(
          this.afFun.httpsCallable('userFunctions')({
            action: 'UPDATE_PASSWORD',
            password: password,
            confirmPassword: confirmPassword
          })
        );
      } catch (error) {
        console.error('Error changing password:', error);
        throw error;
      }
    } else {
      throw new Error('No authenticated user found');
    }
  }
}
